'use es6';

import * as DisplayTypes from 'customer-data-filters/filterQueryFormat/DisplayTypes';
import { Map as ImmutableMap } from 'immutable';
import { getOperatorTranslationKey } from './FilterOperatorTranslations';
import { isOperatorIncludesObjectsWithNoValueSet } from '../../utilities/defaultNullValue';
import DisplayValueAssociation from '../display/DisplayValueAssociation';
import DisplayValueBoolean from '../display/DisplayValueBoolean';
import DisplayValueCta from 'customer-data-filters/components/display/DisplayValueCta';
import DisplayValueDate from '../display/DisplayValueDate';
import DisplayValueDuration from '../display/DisplayValueDuration';
import DisplayValueEmailLink from '../display/DisplayValueEmailLink';
import DisplayValueEnumeration from '../display/DisplayValueEnumeration';
import DisplayValueExternalOption from '../display/DisplayValueExternalOption';
import DisplayValueNumber from '../display/DisplayValueNumber';
import DisplayValuePercentage from '../display/DisplayValuePercentage';
import DisplayValueProperty from '../display/DisplayValueProperty';
import DisplayValueRollingDate from '../display/DisplayValueRollingDate';
import DisplayValueRollingInequality from '../display/DisplayValueRollingInequality';
import DisplayValueRollingInequalityWithUnit from '../display/DisplayValueRollingInequalityWithUnit';
import DisplayValueRollingPropertyUpdated from '../display/DisplayValueRollingPropertyUpdated';
import DisplayValueText from '../display/DisplayValueText';
import DisplayValueRegex from '../display/DisplayValueRegex';
import FieldDefinitionType from '../propTypes/FieldDefinitionType';
import FilterOperatorType from 'customer-data-filters/components/propTypes/FilterOperatorType';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import RestrictedAsset from '../RestrictedAsset';
import { Fragment, PureComponent } from 'react';
import UILink from 'UIComponents/link/UILink';
import emptyFunction from 'react-utils/emptyFunction';
import getIn from 'transmute/getIn';
import { isExternalOptionsField } from '../../utilities/isExternalOptionsField';
import { isExternalOptionsEnum } from '../../utilities/isExternalOptionsEnum';
import * as UnifiedEventPropertyTypes from 'customer-data-filters/filterQueryFormat/UnifiedEventPropertyTypes';
import { __ANY_CTA } from '../../converters/listSegClassic/ListSegConstants';
import { CANDY_APPLE, OZ, OZ_MEDIUM } from 'HubStyleTokens/colors';
import MatchStatusIconCircle from 'customer-data-filters/components/operator/MatchStatusIconCircle';
import { MissingAssociationBranchFilter } from '../../filterQueryFormat/operator/Operators';
import DisplayMissingAssociationBranchFilter from '../display/DisplayMissingAssociationBranchFilter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HIGH_VALUE_FIELD = 'highValue';
const VALUE_FIELD = 'value';
const styles = {
  bold: {
    fontWeight: '600'
  },
  pass: {
    backgroundColor: OZ_MEDIUM,
    paddingBottom: '4px',
    paddingLeft: '2px',
    paddingRight: '2px',
    paddingTop: '4px',
    whiteSpace: 'nowrap'
  },
  fail: {
    paddingBottom: '4px',
    paddingTop: '4px',
    whiteSpace: 'nowrap'
  }
};
const matchDataByStatus = {
  [false]: {
    icon: 'remove',
    iconColor: CANDY_APPLE,
    style: styles.fail
  },
  [true]: {
    icon: 'success',
    iconColor: OZ,
    style: styles.pass
  }
};
export const getValueComponent = (fieldDefinitions, operator, filterFamily) => {
  const field = operator.field;
  const CustomValueComponent = getIn([field.name, 'ValueComponent'], fieldDefinitions);
  if (CustomValueComponent) {
    return CustomValueComponent;
  }
  if (field.type === 'number' && field.numberDisplayHint === 'duration' && operator.constructor.displayType === DisplayTypes.DefaultDisplayType) {
    return DisplayValueDuration;
  }
  if (field.type === 'number' && field.numberDisplayHint === 'percentage' && operator.constructor.displayType === DisplayTypes.DefaultDisplayType) {
    return DisplayValuePercentage;
  }
  if (operator.constructor.displayType === DisplayTypes.MomentDate) {
    return DisplayValueDate;
  }
  const fieldType = getIn([field.name, 'inputType'], fieldDefinitions) || getIn([field.type, 'inputType'], fieldDefinitions) || field.displayType || field.type;
  const displayType = operator.constructor.displayType === DisplayTypes.DefaultDisplayType ? fieldType : operator.constructor.displayType;
  if (isExternalOptionsEnum(field) && isExternalOptionsField(field, filterFamily)) {
    // some operators need a special display component
    if (displayType === DisplayTypes.NumberDisplayType || displayType === DisplayTypes.StringDisplayType) {
      return DisplayValueExternalOption;
    }
  }
  switch (displayType) {
    case DisplayTypes.AssociationDisplayType:
      return DisplayValueAssociation;
    case DisplayTypes.BooleanDisplayType:
      return DisplayValueBoolean;
    case DisplayTypes.CtaDisplayType:
      return DisplayValueCta;
    case DisplayTypes.DateDisplayType:
    case DisplayTypes.DatetimeDisplayType:
      return DisplayValueDate;
    case DisplayTypes.EmailLinkDisplayType:
      return DisplayValueEmailLink;
    case DisplayTypes.EmailSubscriptionDisplayType:
    case DisplayTypes.EnumerationDisplayType:
    case UnifiedEventPropertyTypes.OBJECT_COORDINATES:
      return isExternalOptionsField(field, filterFamily) ? DisplayValueExternalOption : DisplayValueEnumeration;
    case DisplayTypes.NumberDisplayType:
      return DisplayValueNumber;
    case DisplayTypes.PercentageDisplayType:
      return DisplayValuePercentage;
    case DisplayTypes.PropertyDisplayType:
      return DisplayValueProperty;
    case DisplayTypes.RollingPropertyUpdatedDisplayType:
      return DisplayValueRollingPropertyUpdated;
    case DisplayTypes.RollingDateDisplayType:
      return DisplayValueRollingDate;
    case DisplayTypes.RollingInequalityDisplayType:
      return DisplayValueRollingInequality;
    case DisplayTypes.RollingWithUnitDisplayType:
      return DisplayValueRollingInequalityWithUnit;
    case DisplayTypes.RegexString:
      return DisplayValueRegex;
    case DisplayTypes.NoDisplayType:
      return undefined;
    default:
      return DisplayValueText;
  }
};
export default class FilterOperatorDisplay extends PureComponent {
  getAssociatedObjectName() {
    const {
      filterFamily,
      getFilterFamilyObjectName
    } = this.props;
    const associatedObjectName = typeof getFilterFamilyObjectName === 'function' ? getFilterFamilyObjectName(filterFamily, true) : filterFamily;
    return associatedObjectName;
  }
  renderLabel() {
    const {
      getLabelString,
      isReadOnly,
      isXoEnabled,
      hideRestrictedAsset,
      filterFamily,
      operator: {
        field
      }
    } = this.props;
    if (hideRestrictedAsset && field.userHasNoAccess) {
      return /*#__PURE__*/_jsx(RestrictedAsset, {
        filterFamily: filterFamily
      });
    }
    const Component = isReadOnly ? 'strong' : UILink;
    if (!isXoEnabled) {
      return /*#__PURE__*/_jsx("span", {
        "data-selenium-test": "FilterOperatorDisplay-filters2",
        children: getLabelString(field, filterFamily)
      });
    }
    return /*#__PURE__*/_jsx(Component, {
      children: getLabelString(field, filterFamily)
    });
  }
  renderValue(fieldName) {
    const {
      currencyCode,
      data,
      fieldDefinitions,
      filterFamily,
      getFamilyValueResolver,
      getReferencedObjectType,
      getSpecialOptionsForReferenceType,
      isReadOnly,
      isXoEnabled,
      matchingFilterValues,
      operator,
      operatorValueDisplayCutoffCount,
      hideRestrictedAsset,
      getIsUngated
    } = this.props;
    if (hideRestrictedAsset && operator.field.userHasNoAccess && operator.value !== __ANY_CTA) {
      return /*#__PURE__*/_jsx(RestrictedAsset, {
        filterDisplayElement: "value",
        filterFamily: filterFamily
      });
    }
    const ValueComponent = getValueComponent(fieldDefinitions, operator, filterFamily);
    const isInexclusive = operator.constructor.isInexclusive(fieldName);
    const isIterable = operator.constructor.isIterableField(fieldName);
    const specialOptions = getSpecialOptionsForReferenceType(getReferencedObjectType(operator));
    const value = operator.get(fieldName);
    const hasValueFieldAndItsEmpty = operator.has(fieldName) && (value === null || value === undefined);
    if (!ValueComponent || hasValueFieldAndItsEmpty) {
      return '';
    }
    const sharedValueComponentProps = {
      currencyCode,
      data,
      fieldDefinitions,
      filterFamily,
      getFamilyValueResolver,
      getReferencedObjectType,
      operator,
      specialOptions,
      getIsUngated
    };
    if (!isIterable) {
      return /*#__PURE__*/_jsx("b", {
        style: styles.bold,
        children: /*#__PURE__*/_jsx(ValueComponent, Object.assign({}, sharedValueComponentProps, {
          fieldName: [fieldName],
          value: value
        }))
      });
    }
    const operatorValueDisplayOverflowCount = isIterable && isXoEnabled && !isReadOnly ? operator.get(fieldName).size - operatorValueDisplayCutoffCount : 0;
    const operatorValues = operator.get(fieldName).valueSeq();
    const operatorDisplayValues = operatorValueDisplayOverflowCount > 1 ? operatorValues.take(operatorValueDisplayCutoffCount) : operatorValues;
    return /*#__PURE__*/_jsx(FormattedList, {
      exclusive: !isInexclusive,
      children: operatorDisplayValues.map((valueItem, i) => {
        const matchData = matchingFilterValues && matchDataByStatus[matchingFilterValues.includes(valueItem)];
        const style = matchingFilterValues ? styles.bold && matchData.style : styles.bold;
        return /*#__PURE__*/_jsxs("b", {
          style: style,
          children: [/*#__PURE__*/_jsx(MatchStatusIconCircle, {
            matchData: matchData
          }), /*#__PURE__*/_jsx(ValueComponent, Object.assign({}, sharedValueComponentProps, {
            fieldName: [fieldName, i],
            value: valueItem
          }))]
        }, i);
      }).concat(...(operatorValueDisplayOverflowCount > 1 ? [/*#__PURE__*/_jsx("b", {
        style: styles.bold,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFilters.FilterEditorOperatorDisplay.valueOverflowCount",
          options: {
            count: operatorValueDisplayOverflowCount
          }
        })
      }, "overflow")] : []))
    });
  }
  renderEmptyInBold() {
    const {
      matchingFilterValues,
      isMatchingEmptyPropertyValue
    } = this.props;
    const matchData = matchingFilterValues && matchDataByStatus[isMatchingEmptyPropertyValue];
    return /*#__PURE__*/_jsxs("span", {
      style: matchData && matchData.style,
      children: [/*#__PURE__*/_jsx(MatchStatusIconCircle, {
        matchData: matchData
      }), /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "customerDataFilters.FilterOperatorTranslations.WITH_UNKNOWN.EmptyInBold"
      })]
    });
  }
  render() {
    const {
      operator,
      operator: {
        constructor: ctor,
        field
      },
      includeObjectsWithNoAssociatedObjects
    } = this.props;
    const labelOptions = {
      // count is used to determine which translation to use, it shouldn't ever be displayed
      count: field.displayType === DisplayTypes.CountDisplayType ? parseInt(operator.get(VALUE_FIELD), 10) : undefined,
      highValue: operator.has(HIGH_VALUE_FIELD) ? this.renderValue(HIGH_VALUE_FIELD) : undefined,
      rollingOffset: operator.has('rollingOffset') ? parseInt(operator.get('rollingOffset') || 0, 10) : undefined,
      label: this.renderLabel(),
      value: this.renderValue(VALUE_FIELD),
      empty: this.renderEmptyInBold()
    };
    if (ctor && ctor.getOperatorDisplay) {
      return ctor.getOperatorDisplay(labelOptions);
    }
    if (ctor === MissingAssociationBranchFilter) {
      return /*#__PURE__*/_jsx(DisplayMissingAssociationBranchFilter, {});
    }
    const addOrUnknown = isOperatorIncludesObjectsWithNoValueSet(operator);
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
        message: getOperatorTranslationKey(ctor, field, addOrUnknown),
        options: labelOptions,
        style: {
          wordWrap: 'break-word'
        }
      }), "\xA0", includeObjectsWithNoAssociatedObjects && /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "customerDataFilters.FilterOperatorInput.includingObjectsWithNoAssociatedObjects",
        options: {
          associatedObjectName: this.getAssociatedObjectName()
        }
      })]
    });
  }
}
FilterOperatorDisplay.defaultProps = {
  fieldDefinitions: ImmutableMap(),
  getFamilyValueResolver: getIn(['field', 'referencedObjectType']),
  getLabelString: any => any.toString(),
  getReferencedObjectType: emptyFunction,
  operatorValueDisplayCutoffCount: 10
};
FilterOperatorDisplay.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  data: ImmutablePropTypes.map,
  fieldDefinitions: ImmutablePropTypes.mapOf(FieldDefinitionType),
  filterFamily: PropTypes.string.isRequired,
  getFamilyValueResolver: PropTypes.func.isRequired,
  getFilterFamilyObjectName: PropTypes.func,
  getIsUngated: PropTypes.func.isRequired,
  getLabelString: PropTypes.func.isRequired,
  getReferencedObjectType: PropTypes.func.isRequired,
  getSpecialOptionsForReferenceType: PropTypes.func.isRequired,
  hideRestrictedAsset: PropTypes.bool,
  includeObjectsWithNoAssociatedObjects: PropTypes.bool,
  isMatchingEmptyPropertyValue: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isXoEnabled: PropTypes.bool,
  matchingFilterValues: PropTypes.arrayOf(PropTypes.string),
  operator: FilterOperatorType.isRequired,
  operatorValueDisplayCutoffCount: PropTypes.number.isRequired
};